import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Observer } from 'gsap/Observer';
import { nanoid } from 'nanoid';

import { Sizes } from '../utils';

gsap.registerPlugin( ScrollTrigger, Observer );

export class Marquee {
	constructor( selector, options = {} ) {
		this.elements = document.querySelectorAll( selector );
		this.container = document.querySelector( selector );
		this.sizes = new Sizes();
		this.options = {
			...options,
			devices: options?.devices || [ 'Desktop', 'Tablet', 'Mobile' ],
		};
		this.isEnable = false;

		this.allParts = [];
		this.triggerID = `e-trigger-${ nanoid( 9 ) }`;
		this.observerID = `e-observer-${ nanoid( 9 ) }`;
		this.timer = null;
		this.init();
	}

	init() {
		if (
			! this.elements?.length ||
			this.isEnable ||
			! this.options.devices.includes( this.sizes.device )
		)
			return;
		this.append();
		ScrollTrigger.getById( this.triggerID )?.kill( true );
		Observer.getById( this.observerID )?.kill( true );
		const config = { repeat: -1 };
		if ( this.options.speed ) config.speed = this.options.speed;
		if ( this.options.reverse ) config.reversed = this.options.reverse;
		this.allParts = gsap.utils.toArray( '.el-part', this.container );
		const tl = this.infinityLoop( this.allParts, config );
		tl.pause();
		if ( this.options.scrollCatch ) {
			Observer.create( {
				id: this.observerID,
				onChangeY: ( self ) => {
					let factor = 2.5;
					if ( config.reversed ) {
						factor *= -1;
					}
					if ( self.deltaY < 0 ) {
						factor *= -1;
					}
					gsap.timeline( {
						defaults: {
							ease: 'none',
						},
					} )
						.to( tl, { timeScale: factor * 2.5, duration: 0.2 } )
						.to(
							tl,
							{ timeScale: factor / 2.5, duration: 1 },
							'+=0.3'
						);
				},
			} );
		}
		ScrollTrigger.create( {
			id: this.triggerID,
			trigger: this.container,
			start: 'top bottom',
			end: 'bottom top',
			onToggle: ( self ) => {
				if ( self.isActive ) {
					tl.play();
					if ( config.reversed ) {
						tl.reverse();
					}
				} else {
					tl.pause();
				}
			},
		} );
		this.isEnable = true;
	}

	append() {
		const wrap = this.container.querySelector( '.el-inner' );
		const originPart = this.container.querySelector( '.el-part' );
		const theInnerHTML = wrap.innerHTML;
		const CopysCount =
			Math.ceil( wrap.offsetWidth / originPart.offsetWidth ) + 1;
		while ( wrap.firstChild ) {
			wrap.removeChild( wrap.firstChild );
		}
		for ( let i = 0; i < CopysCount; i++ ) {
			wrap.insertAdjacentHTML( 'beforeend', theInnerHTML );
		}
	}

	kill() {
		if ( ! this.isEnable ) return;
	}

	resize() {
		this.kill();
		this.init();
	}

	destroy() {
		ScrollTrigger.getById( this.triggerID )?.kill( true );
		Observer.getById( this.observerID )?.kill( true );
		gsap.killTweensOf( this.allParts );
		gsap.set( this.allParts, { clearProps: 'all' } );
		this.isEnable = false;
	}

	infinityLoop( items, config ) {
		items = gsap.utils.toArray( items );
		config = config || {};
		const tl = gsap.timeline( {
				repeat: config.repeat,
				paused: config.paused,
				defaults: { ease: 'none' },
				onReverseComplete: () =>
					tl.totalTime( tl.rawTime() + tl.duration() * 100 ),
			} ),
			length = items.length,
			startX = items[ 0 ].offsetLeft,
			times = [],
			widths = [],
			xPercents = [],
			pixelsPerSecond = ( config.speed || 1 ) * 100,
			snap =
				config.snap === false
					? ( v ) => v
					: gsap.utils.snap( config.snap || 1 );
		let curIndex = 0,
			curX,
			distanceToStart,
			distanceToLoop,
			item,
			i;
		gsap.set( items, {
			xPercent: ( index, el ) => {
				const w = ( widths[ index ] = parseFloat(
					gsap.getProperty( el, 'width', 'px' )
				) );
				xPercents[ index ] = snap(
					( parseFloat( gsap.getProperty( el, 'x', 'px' ) ) / w ) *
						100 +
						gsap.getProperty( el, 'xPercent' )
				);
				return xPercents[ index ];
			},
		} );
		gsap.set( items, { x: 0 } );
		const totalWidth =
			items[ length - 1 ].offsetLeft +
			( xPercents[ length - 1 ] / 100 ) * widths[ length - 1 ] -
			startX +
			items[ length - 1 ].offsetWidth *
				gsap.getProperty( items[ length - 1 ], 'scaleX' ) +
			( parseFloat( config.paddingRight ) || 0 );
		for ( i = 0; i < length; i++ ) {
			item = items[ i ];
			curX = ( xPercents[ i ] / 100 ) * widths[ i ];
			distanceToStart = item.offsetLeft + curX - startX;
			distanceToLoop =
				distanceToStart +
				widths[ i ] * gsap.getProperty( item, 'scaleX' );
			tl.to(
				item,
				{
					xPercent: snap(
						( ( curX - distanceToLoop ) / widths[ i ] ) * 100
					),
					duration: distanceToLoop / pixelsPerSecond,
				},
				0
			)
				.fromTo(
					item,
					{
						xPercent: snap(
							( ( curX - distanceToLoop + totalWidth ) /
								widths[ i ] ) *
								100
						),
					},
					{
						xPercent: xPercents[ i ],
						duration:
							( curX - distanceToLoop + totalWidth - curX ) /
							pixelsPerSecond,
						immediateRender: false,
					},
					distanceToLoop / pixelsPerSecond
				)
				.add( 'label' + i, distanceToStart / pixelsPerSecond );
			times[ i ] = distanceToStart / pixelsPerSecond;
		}
		function toIndex( index, vars ) {
			vars = vars || {};
			const newIndex = gsap.utils.wrap( 0, length, index );
			let time = times[ newIndex ];
			if ( time > tl.time() !== index > curIndex ) {
				vars.modifiers = { time: gsap.utils.wrap( 0, tl.duration() ) };
				time += tl.duration() * ( index > curIndex ? 1 : -1 );
			}
			curIndex = newIndex;
			vars.overwrite = true;
			return tl.tweenTo( time, vars );
		}
		tl.next = ( vars ) => toIndex( curIndex + 1, vars );
		tl.previous = ( vars ) => toIndex( curIndex - 1, vars );
		tl.current = () => curIndex;
		tl.toIndex = ( index, vars ) => toIndex( index, vars );
		tl.times = times;
		tl.progress( 1, true ).progress( 0, true );
		if ( config.reversed ) {
			tl.vars.onReverseComplete();
			tl.reverse();
		}
		return tl;
	}
}
